<template>
  <div class="bonus-awans" :class="{ 'bonus-awans--subpage': isSubpage }">
    <div class="bonus-awans__header">
      <div class="bonus-awans__heading">
        <span class="bonus-awans__badge">
          <img :src="bonusIconSrc" alt="" aria-hidden="true" />
        </span>
        <h3 class="bonus-awans__title">{{ $t(titleKey) }}</h3>
      </div>
    </div>

    <div class="bonus-awans__progress">
      <div class="bonus-awans__track">
        <div class="bonus-awans__bar-wrap">
          <div class="bonus-awans__labels-band">
            <span
              v-for="milestone in safeMilestones"
              :key="milestone.threshold"
              class="bonus-awans__progress-label bonus-awans__progress-label--points"
              :class="{
                'bonus-awans__progress-label--achieved': isMilestoneAchieved(milestone),
              }"
              :style="{ left: getMilestonePosition(milestone.threshold) + '%' }"
            >
              <span
                v-if="isMilestoneAchieved(milestone)"
                class="bonus-awans__points-check"
                aria-hidden="true"
              >
                <i class="fas fa-check"></i>
              </span>
              {{ $t(milestone.pointsLabelKey) }}
            </span>
          </div>
          <div class="bonus-awans__bar">
            <div
              v-for="(seg, i) in barSegments"
              :key="`seg-${i}`"
              class="bonus-awans__track-segment"
              :class="{
                'bonus-awans__track-segment--tail':
                  !seg.gapRight && isSubpage,
              }"
              :style="segmentStyle(seg)"
            ></div>
            <template v-if="showFirstAchievedUi">
              <div
                class="bonus-awans__progress-fill"
                :style="firstReachedFillStyle"
              ></div>
              <div
                class="bonus-awans__progress-gap"
                :style="{
                  left: firstMilestoneLeftPct + '%',
                  width: MILESTONE_GAP_PX + 'px',
                }"
                aria-hidden="true"
              ></div>
              <div
                class="bonus-awans__progress-fill"
                :style="beyondFirstFillStyle"
              ></div>
            </template>
            <div
              v-else
              class="bonus-awans__progress-fill"
              :style="{ width: progressPct + '%' }"
            ></div>
            <div
              v-for="milestone in visibleMarkers"
              :key="`marker-${milestone.threshold}`"
              class="bonus-awans__marker"
              :style="{ left: getMilestonePosition(milestone.threshold) + '%' }"
            ></div>
          </div>
          <span
            class="bonus-awans__units-under-bar"
            :style="progressUnitsLabelStyle"
          >{{ $t("portalHome.units.piecesCount", { count: data.currentUnits }) }}</span>
        </div>
        <div class="bonus-awans__goal-under">
          <span
            v-for="(milestone, index) in safeMilestones"
            :key="`${milestone.threshold}-${index}`"
            class="bonus-awans__goal-pill"
            :class="[
              goalPillAlignClass(getMilestonePosition(milestone.threshold)),
              isMilestoneAchieved(milestone) ? 'bonus-awans__goal-pill--achieved' : '',
            ]"
            :style="goalPillStyle(milestone)"
          >
            <template v-if="isMilestoneAchieved(milestone)">
              {{ $t(achievedMilestoneLabelKey, { count: milestone.threshold }) }}
            </template>
            <template v-else-if="showFirstAchievedUi && index === 1">
              {{ $t(remainingMilestoneLabelKey, { count: remainingToNextMilestone }) }}
            </template>
            <template v-else>
              {{ $t(milestone.labelKey) }}
            </template>
          </span>
        </div>
      </div>
    </div>

    <div class="bonus-awans__footer" :class="{ 'bonus-awans__footer--subpage': isSubpage }">
      <template v-if="isSubpage">
        <p class="bonus-awans__period bonus-awans__period--split">
          <span class="bonus-awans__period-lead">{{ $t(`${periodBlockPrefix}.periodLead`) }}</span>
          <strong class="bonus-awans__period-dates">{{ $t(`${periodBlockPrefix}.periodDates`) }}</strong>
          <span class="bonus-awans__period-note">{{ $t(`${periodBlockPrefix}.periodNote`) }}</span>
        </p>
        <button type="button" class="bonus-awans__regs">{{ $t("portalHome.bonusUhrd.regsButton") }}</button>
      </template>
      <RouterLink
        v-else
        class="bonus-awans__cta bonus-awans__cta--route"
        :to="{ name: 'bonusAwans' }"
      >
        {{ $t("portalHome.bonusAwans.detailsButton") }}
      </RouterLink>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from "vue";
import { RouterLink } from "vue-router";
import { portalHomeIcons } from "./portalHomeAssets";

const props = defineProps({
  data: { type: Object, required: true },
  titleKey: { type: String, default: "portalHome.bonusAwans.title" },
  variant: {
    type: String,
    default: "home",
    validator: (v) => v === "home" || v === "subpage",
  },
  periodLabelKey: {
    type: String,
    default: "portalHome.bonusAwansPage.bonusPeriodLine",
  },
  periodBlockPrefix: {
    type: String,
    default: "portalHome.bonusAwansPage",
  },
});

const isSubpage = computed(() => props.variant === "subpage");

const bonusIconSrc = portalHomeIcons.bonusAwans;

const showFirstAchievedUi = computed(() => props.data.firstMilestoneAchieved === true);

const achievedMilestoneLabelKey =
  "portalHome.bonusAwans.milestones.goal.achievedLabel";
const remainingMilestoneLabelKey =
  "portalHome.bonusAwans.milestones.challenge.remainingLabel";

const safeMilestones = computed(() =>
  Array.isArray(props.data.milestones) ? props.data.milestones : []
);

const safeMaxUnits = computed(() => {
  const n = Number(props.data.maxUnits);
  return Number.isFinite(n) && n > 0 ? n : 400;
});

const TAIL_RATIO = 0.08;

const positionScale = computed(() => safeMaxUnits.value / (1 - TAIL_RATIO));

const progressPct = computed(() =>
  Math.min(
    ((Number(props.data.currentUnits) || 0) / positionScale.value) * 100,
    100
  )
);

const firstMilestoneThreshold = computed(() => {
  const first = safeMilestones.value[0];
  const t = Number(first?.threshold);
  return Number.isFinite(t) ? t : 0;
});

const firstMilestoneLeftPct = computed(() =>
  getMilestonePosition(firstMilestoneThreshold.value)
);

const remainingToNextMilestone = computed(() => {
  const current = Number(props.data.currentUnits) || 0;
  const next = safeMilestones.value.find((m) => current < Number(m.threshold));
  if (!next) return 0;
  return Math.max(0, Number(next.threshold) - current);
});

const progressUnitsLabelStyle = computed(() => {
  const p = Math.min(Math.max(progressPct.value, 0), 100);
  if (p <= 0) {
    return { left: "0%", transform: "translateX(0)" };
  }
  if (showFirstAchievedUi.value && p < 14) {
    return { left: `${p}%`, transform: "translateX(0)", marginLeft: "2px" };
  }
  if (showFirstAchievedUi.value) {
    return { left: `${p}%`, transform: "translateX(calc(-100% - 2px))" };
  }
  return { left: `${p}%`, transform: "translateX(-50%)" };
});

const getMilestonePosition = (threshold) =>
  Math.min((threshold / positionScale.value) * 100, 100);

function isMilestoneAchieved(milestone) {
  if (!showFirstAchievedUi.value) return false;
  const current = Number(props.data.currentUnits) || 0;
  return current >= Number(milestone.threshold);
}

const visibleMarkers = computed(() =>
  safeMilestones.value.filter((m) => !isMilestoneAchieved(m))
);

const MARKER_RADIUS_PX = 18;
const GAP_AFTER_MARKER_PX = 12;
const MILESTONE_GAP_PX = 10;

const milestoneGapHalfPx = computed(() => MILESTONE_GAP_PX / 2);

const firstReachedFillStyle = computed(() => {
  const pct = firstMilestoneLeftPct.value;
  if (pct <= 0) return { width: "0" };
  return { width: `calc(${pct}% - ${milestoneGapHalfPx.value}px)` };
});

const beyondFirstFillStyle = computed(() => {
  const start = firstMilestoneLeftPct.value;
  const end = progressPct.value;
  if (end <= start) return { display: "none" };
  return {
    left: `calc(${start}% + ${milestoneGapHalfPx.value}px)`,
    width: `calc(${end - start}% - ${MILESTONE_GAP_PX}px)`,
  };
});

const barSegments = computed(() => {
  if (showFirstAchievedUi.value) {
    const g1 = firstMilestoneLeftPct.value;
    const positions = safeMilestones.value
      .filter((m) => !isMilestoneAchieved(m))
      .map((m) => getMilestonePosition(m.threshold))
      .filter((p) => Number.isFinite(p))
      .sort((a, b) => a - b);

    const segs = [
      {
        startPct: 0,
        endPct: g1,
        gapLeft: false,
        gapRight: true,
        gapRightAtGoal: true,
      },
    ];

    let prev = g1;
    positions.forEach((p) => {
      segs.push({
        startPct: prev,
        endPct: p,
        gapLeft: true,
        gapLeftAtGoal: prev === g1,
        gapRight: true,
        gapRightAtGoal: false,
      });
      prev = p;
    });

    segs.push({
      startPct: prev,
      endPct: 100,
      gapLeft: true,
      gapLeftAtGoal: prev === g1,
      gapRight: false,
      gapRightAtGoal: false,
    });

    return segs.filter((s) => s.endPct > s.startPct);
  }

  const positions = safeMilestones.value
    .map((m) => getMilestonePosition(m.threshold))
    .filter((p) => Number.isFinite(p))
    .sort((a, b) => a - b);
  const segs = [];
  let prev = 0;
  let prevHasMarker = false;
  positions.forEach((p) => {
    segs.push({
      startPct: prev,
      endPct: p,
      gapLeft: prevHasMarker,
      gapRight: true,
      gapLeftAtGoal: false,
      gapRightAtGoal: false,
    });
    prev = p;
    prevHasMarker = true;
  });
  segs.push({
    startPct: prev,
    endPct: 100,
    gapLeft: prevHasMarker,
    gapRight: false,
    gapLeftAtGoal: false,
    gapRightAtGoal: false,
  });
  return segs.filter((s) => s.endPct > s.startPct);
});

const segmentStyle = (seg) => {
  const achGap = milestoneGapHalfPx.value;
  const markerInset = MARKER_RADIUS_PX + GAP_AFTER_MARKER_PX;

  return {
    left: !seg.gapLeft
      ? `${seg.startPct}%`
      : seg.gapLeftAtGoal
        ? `calc(${seg.startPct}% + ${achGap}px)`
        : `calc(${seg.startPct}% + ${markerInset}px)`,
    right: !seg.gapRight
      ? `${100 - seg.endPct}%`
      : seg.gapRightAtGoal
        ? `calc(${100 - seg.endPct}% + ${achGap}px)`
        : `calc(${100 - seg.endPct}% - ${MARKER_RADIUS_PX}px)`,
  };
};

function goalPillAlignClass(pct) {
  if (pct >= 78) return "bonus-awans__goal-pill--end";
  if (pct <= 12) return "bonus-awans__goal-pill--start";
  return "";
}

function goalPillStyle(milestone) {
  if (isMilestoneAchieved(milestone)) {
    return {
      left: `${getMilestonePosition(milestone.threshold)}%`,
      transform: "translateX(-50%)",
    };
  }
  return { left: `${getMilestonePosition(milestone.threshold)}%` };
}
</script>

<style scoped lang="scss">
@import "@/styles/portal-home/bonus-awans-widget";
</style>
